/**
 * @version 1.0.0
 * @created 2026-02-01
 * @updated 2026-07-01
 * @url /signin
 * @description Sign in page with Supabase Auth (ID/Password + Google OAuth)
 *              Includes session management and audit logging
 */
import clsx from "clsx";
import forge from "node-forge";
import { useRouter } from "next/router";
import { createHash } from "crypto";
import type { GetServerSidePropsContext } from "next";
import React, { useState, useEffect, useRef } from "react";
// Components ( HeroUI )
import { Icon } from "@iconify/react";
import { Form, Input, Button, Spinner, Divider } from "@heroui/react";
// Services
import { createCSRClient } from "@/services/supabase";
import { extractErrorFromURL, mapErrorToMessage, cleanErrorFromURL, extractErrorFromCookie, cleanErrorCookie, isAllowedRedirectRoute } from "@/constants";
// Sources
import { pem } from "@/source/node";
import { env, auth } from "@/source";

// ─── Types ────────────────────────────────────────────────────────────────────

interface SessionInfo {
  active_sessions: number;
  max_sessions: number;
  revoked_session: boolean;
}

/**
 * Validates redirect URL to prevent open redirect vulnerabilities
 * @param redirect - Redirect URL from query parameter
 * @returns Validated redirect URL or "/" as fallback
 */
function validateRedirectUrl(redirect: string | undefined): string {
  const DEFAULT_REDIRECT = "/";

  if (!redirect || typeof redirect !== "string") {
    return DEFAULT_REDIRECT;
  }

  // Must be relative path
  if (!redirect.startsWith("/")) {
    console.warn(`[Auth] Invalid redirect URL (not relative): ${redirect}`);

    return DEFAULT_REDIRECT;
  }

  // Prevent external URLs (//example.com)
  if (redirect.startsWith("//")) {
    console.warn(`[Auth] Invalid redirect URL (external): ${redirect}`);

    return DEFAULT_REDIRECT;
  }

  // Decode and validate
  let decodedUrl: string;

  try {
    decodedUrl = decodeURIComponent(redirect);
    if (!decodedUrl.startsWith("/") || decodedUrl.startsWith("//")) {
      console.warn(`[Auth] Invalid redirect URL after decode: ${decodedUrl}`);

      return DEFAULT_REDIRECT;
    }
  } catch (error) {
    console.warn(`[Auth] Failed to decode redirect URL: ${redirect}`);

    return DEFAULT_REDIRECT;
  }

  // Check if route is in whitelist (including dynamic routes)
  if (!isAllowedRedirectRoute(decodedUrl)) {
    console.warn(`[Auth] Redirect URL not in whitelist: ${decodedUrl}`);

    return DEFAULT_REDIRECT;
  }

  return decodedUrl;
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  try {
    await auth.page(context);

    return {
      redirect: { destination: "/signin", permanent: false },
    };
  } catch (error: any) {
    const salt = env.key("DB_PASSWORD_SALT");
    const publicPem = await pem.signin.public;

    return { props: { salt, publicPem } };
  }
}

// ─── Render ───────────────────────────────────────────────────────────────────

export default function SigninPage({ publicPem, salt }: { publicPem: string; salt: string }) {
  const router = useRouter();

  const [isLoad, setLoad] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);
  const [phoneValue, setPhoneValue] = useState("");

  // Prevent duplicate callback API calls (React StrictMode + OAuth state changes)
  const isProcessingAuth = useRef(false);

  const toggleVisibility = () => setIsVisible(!isVisible);

  const handlePhoneChange = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 11);
    let formatted = digits;

    if (digits.length > 3) {
      formatted = digits.slice(0, 3) + "-";
      formatted += digits.length > 7 ? digits.slice(3, 7) + "-" + digits.slice(7) : digits.slice(3);
    }
    setPhoneValue(formatted);
  };

  // Get redirect URL from window.location.search (more reliable than router.query)
  const getRedirectUrl = () => {
    // Parse redirect parameter from URL search params
    if (typeof window !== "undefined") {
      const searchParams = new URLSearchParams(window.location.search);
      const redirect = searchParams.get("redirect");

      return validateRedirectUrl(redirect || undefined);
    }

    return "/";
  };

  // Register session with callback API
  const createSession = async (accessToken: string, refreshToken: string | undefined, loginType: "email" | "oauth") => {
    let network = { ip: "", country_code: "", country_name: "" };

    try {
      const geoRes = await fetch("https://geolocation-db.com/json/");
      const { IPv4: ip, country_code, country_name } = await geoRes.json();

      network = { ip, country_code, country_name };
    } catch (_) {}

    const response = await fetch("/api/v1/auth/callback", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        access_token: accessToken,
        refresh_token: refreshToken,
        login_type: loginType,
        network,
      }),
    });

    const result = await response.json();

    if (!response.ok) {
      return {
        success: false,
        message: result.message || "세션 등록에 실패했습니다.",
      };
    }

    // Check if a session was revoked
    if (result.data?.session_info) {
      setSessionInfo(result.data.session_info);
    }

    return { success: true, data: result.data };
  };

  // Handle ID/Password sign in
  const handleIdSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const phone = phoneValue;
    const password = formData.get("password") as string;

    setLoad(true);
    setErrorMessage("");
    setSessionInfo(null);

    try {
      const body = {
        phone,
        password: createHash("sha256").update(`${password}${salt}`).digest("hex"),
      };
      const publicKey = forge.pki.publicKeyFromPem(publicPem);
      const ciphertext = publicKey.encrypt(forge.util.encodeUtf8(JSON.stringify(body)), "RSA-OAEP", {
        md: forge.md.sha256.create(),
        mgf1: { md: forge.md.sha256.create() },
      });

      const response = await fetch("/api/v1/auth/signin", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ciphertext }),
      });

      const result = await response.json();

      if (!response.ok) throw result.message || "로그인에 실패했습니다.";

      if (result.data?.session_info) {
        setSessionInfo(result.data.session_info);
      }

      router.push(getRedirectUrl());
    } catch (error: any) {
      setErrorMessage(typeof error === "string" ? error : "로그인에 실패했습니다.");
      setLoad(false);
    }
  };

  // Handle Google OAuth sign in
  const handleGoogleSignIn = async () => {
    setLoad(true);
    setErrorMessage("");
    setSessionInfo(null);

    try {
      const supabase = createCSRClient();
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/signin${window.location.search}`,
        },
      });

      if (error) {
        throw error.message;
      }
    } catch (error: any) {
      setErrorMessage(typeof error === "string" ? error : "Google 로그인에 실패했습니다.");
      setLoad(false);
    }
  };

  useEffect(() => {
    // delete cookie after extraction to prevent reuse
    const errorFromCookie = extractErrorFromCookie();

    if (errorFromCookie) {
      setErrorMessage(errorFromCookie);
      // clean up cookie ( prevent reuse )
      cleanErrorCookie();

      return;
    }

    // extract error info from URL ( for OAuth callback etc - backward compatibility )
    const { error, errorDescription } = extractErrorFromURL();
    const errorMessage = mapErrorToMessage(error, errorDescription);

    if (errorMessage) {
      setErrorMessage(errorMessage);
      // Clean up URL
      setTimeout(() => {
        cleanErrorFromURL();
      }, 1000);
    }
  }, []);

  // Handle OAuth callback and check session
  useEffect(() => {
    const supabase = createCSRClient();

    // Listen for auth state changes (handles OAuth callback)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === "SIGNED_IN" && session) {
        // Prevent duplicate calls (React StrictMode + multiple state changes)
        if (isProcessingAuth.current) {
          return;
        }

        isProcessingAuth.current = true;
        setLoad(true);

        try {
          // register session with callback API
          const result = await createSession(session.access_token, session.refresh_token, "oauth");

          if (!result.success) {
            await setErrorMessage(result.message);
            setLoad(false);
          }

          // Get redirect URL BEFORE clearing URL
          const redirectPath = getRedirectUrl();

          // Redirect to the original destination
          router.push(redirectPath);
        } catch (error: any) {
          // Failed to register session - sign out and show error
          setErrorMessage(typeof error === "string" ? error : error.message || "세션 등록에 실패했습니다. 다시 시도해주세요.");
          setLoad(false);
        } finally {
          // Reset flag after 2 seconds to allow re-login
          setTimeout(() => {
            isProcessingAuth.current = false;
          }, 2000);
        }
      }
    });

    // Check if already authenticated
    const checkSession = async () => {
      // Skip if already processing authentication
      if (isProcessingAuth.current) {
        console.log("[Auth] Already processing, skipping session check");

        return;
      }

      // Skip redirect if OAuth callback is in progress (tokens in URL hash)
      if (window.location.hash.includes("access_token")) {
        console.log("[Auth] OAuth callback in progress, skipping redirect");

        return;
      }

      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session) {
        isProcessingAuth.current = true;
        router.push(getRedirectUrl());
      }
    };

    checkSession();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return (
    <React.Fragment>
      {/* Loading overlay */}
      <div className={clsx("w-[100vw] h-[100vh] bg-black/80 z-50", isLoad && "fixed", !isLoad && "hidden")}>
        <div className="flex h-[100vh] items-center justify-center flex-col gap-4">
          <Spinner classNames={{ label: "mt-4" }} size="lg" variant="spinner" />
          <p className="text-white">로그인 중</p>
          {sessionInfo?.revoked_session && <p className="text-sm text-warning">세션 제한으로 다른 기기에서 로그아웃되었습니다.</p>}
        </div>
      </div>

      {/* Sign in form */}
      <div className="flex items-center justify-center w-full h-full px-6 pt-12 md:px-0 md:pt-36">
        <div className="flex flex-col w-full max-w-sm gap-4 px-2 pt-4 pb-4 rounded-large bg-content1 shadow-small">
          <div className="flex items-center justify-center w-full h-full">
            <div className="flex flex-col w-full max-w-sm gap-4 px-8 pt-6 pb-10 rounded-large">
              <div className="flex flex-col items-center pb-2">
                <p className="text-2xl font-semibold">BRIDGE ZIPLINE CMS</p>
                <p className="mt-1 text-sm text-default-500">로그인</p>
              </div>

              {/* Error message */}
              {errorMessage && <div className="p-3 text-sm text-danger bg-danger-50 rounded-medium">{errorMessage}</div>}

              {/* Session info message */}
              {sessionInfo && !isLoad && (
                <div className="p-3 text-sm text-warning bg-warning-50 rounded-medium">
                  <div className="flex items-center gap-2">
                    <Icon icon="solar:info-circle-linear" />
                    <span>
                      활성 세션: {sessionInfo.active_sessions}/{sessionInfo.max_sessions}
                    </span>
                  </div>
                </div>
              )}

              {/* ID/Password form */}
              <Form className="flex flex-col gap-4" validationBehavior="native" onSubmit={handleIdSignIn}>
                <Input isRequired label="전화번호" labelPlacement="outside" maxLength={13} placeholder="010-0000-0000" startContent={<span className="text-sm text-default-400 whitespace-nowrap">+82)</span>} type="tel" value={phoneValue} variant="bordered" onValueChange={handlePhoneChange} />
                <Input
                  isRequired
                  endContent={
                    <button type="button" onClick={toggleVisibility}>
                      {isVisible ? <Icon className="text-2xl pointer-events-none text-default-400" icon="solar:eye-closed-linear" /> : <Icon className="text-2xl pointer-events-none text-default-400" icon="solar:eye-bold" />}
                    </button>
                  }
                  label="비밀번호"
                  labelPlacement="outside"
                  name="password"
                  placeholder="비밀번호를 입력하세요"
                  type={isVisible ? "text" : "password"}
                  variant="bordered"
                />
                <Button className="w-full mt-2" color="primary" type="submit">
                  로그인
                </Button>
              </Form>

              {/* Divider */}
              <div className="flex items-center gap-4 py-2">
                <Divider className="flex-1" />
                <span className="text-tiny text-default-400">또는</span>
                <Divider className="flex-1" />
              </div>

              {/* Google OAuth button */}
              <Button className="w-full" startContent={<Icon className="text-xl" icon="flat-color-icons:google" />} variant="bordered" onPress={handleGoogleSignIn}>
                Google로 로그인
              </Button>

              {/* Info text */}
              <p className="mt-4 text-xs text-center text-default-400">회원가입은 관리자의 초대를 통해서만 가능합니다.</p>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}
